'use client';

import { MutableRefObject, ReactNode, useEffect, useMemo, useRef } from 'react';

import { useAuth } from '@r123/auth';
import { useTracker } from '@r123/segment-tracker';

import DefaultToast, {
    ToastElement,
} from '@/app/_components/Toast/DefaultToast';
import { DEFAULT_IMAGE } from '@/app/_constant/component.constant';
import { ListingEvents } from '@/app/_tracker/eventMapping';
import {
    GetCollectionByUuidResponse,
    Property,
} from '@/modules/core/gql/generated';
import { SegmentTrackerEventEnum } from '@/modules/segment/segment.interface';

import { collectionSocialMedias } from '../../_constant/CollectionSocialMediaConstant';
import generateCollectionDetailUrl from '../../_helper/generateCollectionDetailUrl';
import CollectionPopover from './Popover';

type Props = {
    collectionDetail: GetCollectionByUuidResponse;
    ctaElem?: ReactNode;
    className?: string;
    arrowClassName?: string;
};

const CollectionShare = ({
    collectionDetail,
    ctaElem,
    className,
    arrowClassName,
}: Props) => {
    const { authProfile } = useAuth();
    const { sendTracker } = useTracker();
    const toastRef: MutableRefObject<ToastElement | undefined> =
        useRef<ToastElement>(undefined);

    useEffect(() => {
        sendTracker(SegmentTrackerEventEnum.EventActionClicked, {
            agent_id: authProfile?.uuid,
            page_type: 'Favorites',
            event_name: ListingEvents.Shares,
            feature_button_name: 'Share',
            placement_name: 'Share Collection',
            feature_collection_title:
                collectionDetail?.collection?.title || 'Semua Properti Favorit',
            feature_collection_listing_count:
                collectionDetail?.collection?.totalListingItems,
        });
    }, []);

    const shareLink = useMemo(() => {
        const targetUrl = generateCollectionDetailUrl(
            'viewer',
            collectionDetail.collection?.shortCode || '',
            collectionDetail.collection?.title || '',
        );

        return targetUrl;
    }, []);

    const getCoverImage = (property?: Property): string => {
        if (!property) return DEFAULT_IMAGE;

        const { medias } = property;

        const coverMedia = (
            medias?.find((item) => item?.mediaType === 'COVER')?.mediaInfo || []
        ).map((x) => x?.formatUrl || x?.mediaUrl || DEFAULT_IMAGE);

        const regulerMedia = (
            medias?.find((item) => item?.mediaType === 'REGULAR')?.mediaInfo ||
            []
        ).map((x) => x?.formatUrl || x?.mediaUrl || DEFAULT_IMAGE);

        const allMedia = [...coverMedia, ...regulerMedia];

        const coverImage =
            allMedia?.[0]?.replace(
                '{width}x{height}-{scale}',
                '720x420-crop',
            ) || DEFAULT_IMAGE;

        return coverImage;
    };

    const title = useMemo(() => {
        const collectionTitle = collectionDetail.collection?.title || '';
        return `Coba cek koleksi properti dari ${collectionTitle} di link ini! `;
    }, []);

    const shareLinks = useMemo(() => {
        const encodedUrl = encodeURIComponent(shareLink);

        const links: { [key: string]: string } = {
            whatsapp: `https://wa.me/?text=${title}%20${encodedUrl}`,
            facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
            instagram: `https://www.instagram.com/?url=${encodedUrl}`,
            x: `https://twitter.com/intent/tweet?text=${title}&url=${encodedUrl}`,
            telegram: `https://t.me/share/url?url=${encodedUrl}&text=${title}`,
            tiktok: `https://www.tiktok.com/share?url=${encodedUrl}`,
            email: `mailto:?subject=${title}&body=${encodedUrl}`,
        };

        return links;
    }, []);

    const image1 = useMemo(() => {
        if (
            collectionDetail.collection?.items &&
            collectionDetail.collection?.items.length > 0 &&
            collectionDetail.collection?.items?.[0].property
        )
            return getCoverImage(
                collectionDetail.collection?.items?.[0].property,
            );

        return DEFAULT_IMAGE;
    }, []);

    const image2 = useMemo(() => {
        if (
            collectionDetail.collection?.items &&
            collectionDetail.collection?.items.length > 1 &&
            collectionDetail.collection?.items?.[1].property
        )
            return getCoverImage(
                collectionDetail.collection?.items?.[1].property,
            );

        return undefined;
    }, []);

    const handleToast = (message: ReactNode): void => {
        toastRef.current?.setContent({
            children: <>{message}</>,
            type: 'success',
        });
        toastRef.current?.setVisible(true);
    };

    return (
        <>
            <CollectionPopover
                className={className}
                arrowClassName={arrowClassName}
                shareMobile={{
                    text: title,
                    title: title,
                    url: shareLink,
                }}
                ctaElem={
                    ctaElem || (
                        <button className='flex gap-2 border border-solid border-[#2951A3] p-2 justify-center cursor-pointer rounded-lg w-10 h-10'>
                            <svg
                                width='24'
                                height='24'
                                viewBox='0 0 24 24'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                            >
                                <path
                                    d='M16.61 21C15.9473 21 15.385 20.7683 14.923 20.305C14.4617 19.841 14.231 19.2777 14.231 18.615C14.231 18.515 14.275 18.2627 14.363 17.858L7.166 13.585C6.95 13.8343 6.68567 14.03 6.373 14.172C6.06033 14.314 5.72467 14.385 5.366 14.385C4.70867 14.385 4.15 14.151 3.69 13.683C3.23 13.215 3 12.654 3 12C3 11.346 3.23 10.785 3.69 10.317C4.15 9.849 4.70867 9.615 5.366 9.615C5.724 9.615 6.05967 9.686 6.373 9.828C6.68633 9.97 6.95067 10.166 7.166 10.416L14.364 6.161C14.3173 6.03167 14.2837 5.90333 14.263 5.776C14.2417 5.648 14.231 5.51733 14.231 5.384C14.231 4.722 14.4633 4.159 14.928 3.695C15.3927 3.23167 15.9567 3 16.62 3C17.2833 3 17.846 3.23233 18.308 3.697C18.77 4.16167 19.0007 4.72567 19 5.389C18.9993 6.05233 18.7677 6.615 18.305 7.077C17.8423 7.539 17.279 7.76967 16.615 7.769C16.2537 7.769 15.9203 7.695 15.615 7.547C15.3097 7.399 15.0497 7.2 14.835 6.95L7.636 11.223C7.68267 11.3523 7.71633 11.481 7.737 11.609C7.75833 11.7363 7.769 11.8667 7.769 12C7.769 12.1333 7.75833 12.2637 7.737 12.391C7.71567 12.5183 7.68233 12.647 7.637 12.777L14.835 17.05C15.0503 16.8 15.3103 16.601 15.615 16.453C15.9203 16.305 16.2537 16.231 16.615 16.231C17.2777 16.231 17.841 16.463 18.305 16.927C18.7683 17.3923 19 17.9567 19 18.62C19 19.2833 18.7677 19.846 18.303 20.308C17.8383 20.77 17.2733 21.0007 16.61 21ZM16.615 20C17.0077 20 17.3367 19.8673 17.602 19.602C17.8673 19.3367 18 19.008 18 18.616C18 18.224 17.8673 17.895 17.602 17.629C17.3367 17.363 17.008 17.2303 16.616 17.231C16.224 17.2317 15.895 17.3643 15.629 17.629C15.363 17.8937 15.2303 18.2223 15.231 18.615C15.2317 19.0077 15.3643 19.3367 15.629 19.602C15.8937 19.8673 16.2217 20 16.615 20ZM5.366 13.384C5.76333 13.384 6.09667 13.2513 6.366 12.986C6.63467 12.7207 6.769 12.392 6.769 12C6.769 11.608 6.63467 11.2793 6.366 11.014C6.09733 10.7487 5.764 10.616 5.366 10.616C4.97867 10.616 4.65433 10.7487 4.393 11.014C4.13167 11.2793 4.00067 11.608 4 12C3.99933 12.392 4.13033 12.721 4.393 12.987C4.65567 13.253 4.98 13.3853 5.366 13.384ZM16.616 6.769C17.008 6.769 17.3367 6.63633 17.602 6.371C17.8673 6.10567 18 5.77667 18 5.384C18 4.99133 17.8673 4.66267 17.602 4.398C17.3367 4.13333 17.008 4.00067 16.616 4C16.224 3.99933 15.895 4.132 15.629 4.398C15.363 4.664 15.2303 4.993 15.231 5.385C15.2317 5.777 15.3643 6.10567 15.629 6.371C15.8937 6.63633 16.2227 6.769 16.616 6.769Z'
                                    fill='#2951A3'
                                />
                            </svg>
                        </button>
                    )
                }
                contentElem={
                    <div className='collection-share flex flex-col gap-4'>
                        <div className='flex gap-2 items-center'>
                            <svg
                                width='20'
                                height='20'
                                viewBox='0 0 20 20'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                            >
                                <path
                                    d='M14.168 18.3334C13.4735 18.3334 12.8832 18.0904 12.3971 17.6042C11.911 17.1181 11.668 16.5279 11.668 15.8334C11.668 15.7501 11.6888 15.5556 11.7305 15.2501L5.8763 11.8334C5.65408 12.0417 5.39714 12.2051 5.10547 12.3234C4.8138 12.4417 4.5013 12.5006 4.16797 12.5001C3.47352 12.5001 2.88325 12.257 2.39714 11.7709C1.91102 11.2848 1.66797 10.6945 1.66797 10.0001C1.66797 9.30564 1.91102 8.71536 2.39714 8.22925C2.88325 7.74314 3.47352 7.50008 4.16797 7.50008C4.5013 7.50008 4.8138 7.55925 5.10547 7.67758C5.39714 7.79591 5.65408 7.95897 5.8763 8.16675L11.7305 4.75008C11.7027 4.65286 11.6855 4.55925 11.6788 4.46925C11.6721 4.37925 11.6685 4.27841 11.668 4.16675C11.668 3.4723 11.911 2.88203 12.3971 2.39591C12.8832 1.9098 13.4735 1.66675 14.168 1.66675C14.8624 1.66675 15.4527 1.9098 15.9388 2.39591C16.4249 2.88203 16.668 3.4723 16.668 4.16675C16.668 4.86119 16.4249 5.45147 15.9388 5.93758C15.4527 6.42369 14.8624 6.66675 14.168 6.66675C13.8346 6.66675 13.5221 6.60758 13.2305 6.48925C12.9388 6.37091 12.6819 6.20786 12.4596 6.00008L6.60547 9.41675C6.63325 9.51397 6.65075 9.60786 6.65797 9.69841C6.66519 9.78897 6.66852 9.88953 6.66797 10.0001C6.66741 10.1106 6.66408 10.2115 6.65797 10.3026C6.65186 10.3937 6.63436 10.4873 6.60547 10.5834L12.4596 14.0001C12.6819 13.7917 12.9388 13.6287 13.2305 13.5109C13.5221 13.3931 13.8346 13.334 14.168 13.3334C14.8624 13.3334 15.4527 13.5765 15.9388 14.0626C16.4249 14.5487 16.668 15.139 16.668 15.8334C16.668 16.5279 16.4249 17.1181 15.9388 17.6042C15.4527 18.0904 14.8624 18.3334 14.168 18.3334ZM14.168 16.6667C14.4041 16.6667 14.6021 16.587 14.7621 16.4276C14.9221 16.2681 15.0019 16.0701 15.0013 15.8334C15.0007 15.5967 14.9207 15.399 14.7613 15.2401C14.6019 15.0812 14.4041 15.0012 14.168 15.0001C13.9319 14.999 13.7341 15.079 13.5746 15.2401C13.4152 15.4012 13.3352 15.599 13.3346 15.8334C13.3341 16.0679 13.4141 16.2659 13.5746 16.4276C13.7352 16.5892 13.933 16.669 14.168 16.6667ZM4.16797 10.8334C4.40408 10.8334 4.60214 10.7534 4.76214 10.5934C4.92214 10.4334 5.00186 10.2356 5.0013 10.0001C5.00075 9.76453 4.92075 9.56675 4.7613 9.40675C4.60186 9.24675 4.40408 9.16675 4.16797 9.16675C3.93186 9.16675 3.73408 9.24675 3.57464 9.40675C3.41519 9.56675 3.33519 9.76453 3.33464 10.0001C3.33408 10.2356 3.41408 10.4337 3.57464 10.5942C3.73519 10.7548 3.93297 10.8345 4.16797 10.8334ZM14.168 5.00008C14.4041 5.00008 14.6021 4.92008 14.7621 4.76008C14.9221 4.60008 15.0019 4.4023 15.0013 4.16675C15.0007 3.93119 14.9207 3.73341 14.7613 3.57341C14.6019 3.41341 14.4041 3.33341 14.168 3.33341C13.9319 3.33341 13.7341 3.41341 13.5746 3.57341C13.4152 3.73341 13.3352 3.93119 13.3346 4.16675C13.3341 4.4023 13.4141 4.60036 13.5746 4.76091C13.7352 4.92147 13.933 5.00119 14.168 5.00008Z'
                                    fill='#868D9A'
                                />
                            </svg>

                            <span className='font-bold text-base leading-6 text-[#0D1A35] '>
                                Bagikan
                            </span>
                        </div>

                        <hr className='border-b-[#E7E8EB] border-b border-solid border-t-0 border-l-0 border-r-0' />

                        <div className='flex gap-4'>
                            <div className='relative pr-2 w-[72px] h-[72px] mr-4'>
                                {image1 && (
                                    <img
                                        className='w-16 h-16 rounded-md absolute -right-4 bottom-0 bg-cover'
                                        src={image1}
                                    />
                                )}

                                {image2 && (
                                    <img
                                        className='w-16 h-16 rounded-md absolute -right-2 bg-cover'
                                        src={image2}
                                    />
                                )}
                            </div>

                            <div className='flex flex-col gap-2'>
                                <span className='overflow-hidden text-ellipsis break-words line-clamp-2 w-[207px] font-normal text-xs leading-4 text-[#0D1A35]'>
                                    {collectionDetail.collection?.title || ''}
                                </span>
                                <span className='overflow-hidden text-ellipsis break-words line-clamp-2 w-[207px] font-normal text-[10px] leading-4 text-[#868D9A]'>
                                    {shareLink}
                                </span>
                            </div>
                        </div>

                        <hr className='border-b-[#E7E8EB] border-b border-solid border-t-0 border-l-0 border-r-0' />

                        <div className='flex flex-col gap-4 py-2'>
                            <div className='flex gap-6 md:gap-0 md:justify-between no-scrollbar overflow-x-auto'>
                                {collectionSocialMedias.map(
                                    ({ icon, id, name }) => {
                                        return (
                                            <div
                                                key={id}
                                                className='cursor-pointer'
                                                onClick={(): void => {
                                                    sendTracker(
                                                        SegmentTrackerEventEnum.EventActionClicked,
                                                        {
                                                            agent_id:
                                                                authProfile?.uuid,
                                                            page_type:
                                                                'Favorites',
                                                            event_name:
                                                                ListingEvents.Shares,
                                                            feature_button_name:
                                                                name,
                                                            placement_name:
                                                                'Share Collection',
                                                            feature_collection_title:
                                                                collectionDetail
                                                                    ?.collection
                                                                    ?.title ||
                                                                'Semua Properti Favorit',
                                                            feature_collection_listing_count:
                                                                collectionDetail
                                                                    ?.collection
                                                                    ?.totalListingItems,
                                                        },
                                                    );
                                                    window.open(
                                                        shareLinks[id],
                                                        '_blank',
                                                    );
                                                }}
                                            >
                                                {icon}
                                            </div>
                                        );
                                    },
                                )}
                            </div>

                            <div className='flex gap-2 cursor-pointer'>
                                <svg
                                    width='24'
                                    height='24'
                                    viewBox='0 0 24 24'
                                    fill='none'
                                    xmlns='http://www.w3.org/2000/svg'
                                >
                                    <path
                                        d='M10.9854 17.6945C11.0902 17.799 11.1735 17.9232 11.2302 18.06C11.287 18.1967 11.3162 18.3433 11.3162 18.4914C11.3162 18.6394 11.287 18.7861 11.2302 18.9228C11.1735 19.0595 11.0902 19.1837 10.9854 19.2883L10.5054 19.7683C9.99583 20.2797 9.38996 20.685 8.7228 20.9608C8.05563 21.2366 7.34041 21.3775 6.61848 21.3751C5.5313 21.3755 4.46843 21.0534 3.56436 20.4495C2.66028 19.8457 1.95562 18.9872 1.53952 17.9828C1.12343 16.9784 1.01459 15.8732 1.22679 14.8069C1.43899 13.7406 1.96268 12.7612 2.73161 11.9926L5.98942 8.73482C6.68236 8.04198 7.54789 7.54695 8.4964 7.30096C9.44492 7.05498 10.4419 7.06698 11.3843 7.33574C12.3266 7.60449 13.1799 8.12023 13.856 8.82955C14.532 9.53887 15.0062 10.416 15.2294 11.3701C15.2664 11.5152 15.274 11.6662 15.2519 11.8143C15.2298 11.9623 15.1783 12.1045 15.1005 12.2324C15.0228 12.3603 14.9202 12.4714 14.799 12.5592C14.6777 12.647 14.5401 12.7097 14.3943 12.7436C14.2485 12.7776 14.0974 12.7821 13.9498 12.7569C13.8023 12.7317 13.6612 12.6772 13.5349 12.5968C13.4087 12.5164 13.2997 12.4116 13.2145 12.2885C13.1292 12.1655 13.0694 12.0266 13.0385 11.8801C12.906 11.3169 12.6256 10.7993 12.226 10.3808C11.8265 9.96225 11.3225 9.65803 10.766 9.49956C10.2096 9.34109 9.62088 9.33412 9.06083 9.47937C8.50077 9.62462 7.98967 9.91682 7.58036 10.3258L4.32255 13.5836C3.86843 14.0373 3.55907 14.6155 3.43359 15.2451C3.30812 15.8747 3.37216 16.5273 3.61762 17.1205C3.86308 17.7137 4.27893 18.2207 4.81258 18.5776C5.34623 18.9344 5.97372 19.1249 6.61567 19.1251C7.04222 19.1264 7.46478 19.043 7.85888 18.8798C8.25298 18.7167 8.6108 18.4769 8.91161 18.1745L9.39067 17.6945C9.49521 17.5895 9.61946 17.5061 9.75629 17.4493C9.89312 17.3924 10.0398 17.3631 10.188 17.3631C10.3362 17.3631 10.4829 17.3924 10.6197 17.4493C10.7566 17.5061 10.8808 17.5895 10.9854 17.6945ZM21.265 4.23482C20.2339 3.20405 18.8357 2.625 17.3777 2.625C15.9197 2.625 14.5215 3.20405 13.4904 4.23482L13.0113 4.71388C12.8 4.92522 12.6812 5.21187 12.6812 5.51075C12.6812 5.80964 12.8 6.09628 13.0113 6.30763C13.2226 6.51897 13.5093 6.6377 13.8082 6.6377C14.1071 6.6377 14.3937 6.51897 14.605 6.30763L15.085 5.82763C15.6942 5.21846 16.5204 4.87623 17.3819 4.87623C18.2434 4.87623 19.0696 5.21846 19.6788 5.82763C20.288 6.4368 20.6302 7.26301 20.6302 8.1245C20.6302 8.986 20.288 9.81221 19.6788 10.4214L16.4154 13.6745C16.1145 13.9769 15.7567 14.2167 15.3626 14.3798C14.9685 14.543 14.546 14.6264 14.1194 14.6251C13.3877 14.6246 12.6776 14.3769 12.1043 13.9222C11.531 13.4675 11.1281 12.8325 10.961 12.1201C10.8934 11.8295 10.713 11.5776 10.4597 11.4199C10.2063 11.2622 9.90071 11.2116 9.61005 11.2792C9.31939 11.3468 9.0675 11.5271 8.90979 11.7805C8.75208 12.0338 8.70148 12.3395 8.76911 12.6301C9.0514 13.8366 9.73296 14.9123 10.7033 15.6828C11.6736 16.4533 12.8757 16.8735 14.1147 16.8751H14.1194C14.8417 16.8771 15.5572 16.7359 16.2245 16.4596C16.8918 16.1833 17.4978 15.7774 18.0072 15.2654L21.265 12.0076C21.7755 11.4973 22.1804 10.8914 22.4567 10.2246C22.7329 9.55774 22.8751 8.84302 22.8751 8.12122C22.8751 7.39943 22.7329 6.6847 22.4567 6.01787C22.1804 5.35103 21.7755 4.74515 21.265 4.23482Z'
                                        fill='#5982D5'
                                    />
                                </svg>

                                <span
                                    onClick={async (): Promise<void> => {
                                        sendTracker(
                                            SegmentTrackerEventEnum.EventActionClicked,
                                            {
                                                agent_id: authProfile?.uuid,
                                                page_type: 'Favorites',
                                                event_name:
                                                    ListingEvents.Shares,
                                                feature_button_name:
                                                    'Copy Link',
                                                placement_name:
                                                    'Share Collection',
                                                feature_collection_title:
                                                    collectionDetail?.collection
                                                        ?.title ||
                                                    'Semua Properti Favorit',
                                                feature_collection_listing_count:
                                                    collectionDetail?.collection
                                                        ?.totalListingItems,
                                            },
                                        );
                                        await navigator.clipboard.writeText(
                                            shareLink,
                                        );

                                        handleToast('Tautan berhasil disalin!');
                                    }}
                                    className='font-bold text-base leading-6 text-center text-[#5982D5]'
                                >
                                    Salin Tautan
                                </span>
                            </div>
                        </div>
                    </div>
                }
            />

            <DefaultToast
                ref={toastRef}
                position='top'
                type='success'
                timeout={2}
                version={2}
            />
        </>
    );
};

export default CollectionShare;
